import React from 'react';

import { ArrayHelpers, Box, FloatingMenu, SystemText } from 'e4shared';

import GoalSummary from 'components/general/goalSummary';
import { useSelector } from 'react-redux';
import { periodName, sortGoalsByPeriod } from 'helpers/goals';
import SetGoals from 'components/general/goalSummary/components/setGoals';
import { apiGet } from 'services';
import { hasModule } from 'helpers/profile';
import posthog from 'posthog-js';

export const CRMGoals = ({
    profile,
    home,
    showSetGoals,
    shown,
    onLoad,
    onNoGoals,
    showNavigation,
    showPaging = true,
    inBox = true,
    setLastPaint,
    breakAt = 450,
}) => {
    const [showGoals, setShowGoals] = React.useState(showSetGoals);
    const [historic, setHistoric] = React.useState(null);
    const [goals, setGoals] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [periodRef, setPeriodRef] = React.useState('Quarterly');
    const [period, setPeriod] = React.useState('Q');
    const [page, setPage] = React.useState(1);

    const container = React.useRef();
    const [width, setWidth] = React.useState(0);

    const lastUpdated = useSelector(state => state.ui.goalsUpdated);
    const goalPeriods = [...new Set(goals.map(item => item.period))];
    const user = useSelector(state => state.user.user);

    const resizeHandler = () => {
        if (container.current) {
            setWidth(container.current.clientWidth);
        }
    };

    React.useEffect(() => {
        window.addEventListener('resize', resizeHandler);

        return () => {
            window.removeEventListener('resize', resizeHandler);
        };
    }, []); // empty array => run only once

    React.useEffect(() => {
        resizeHandler();
    }, [container.current]);

    const loadGoals = () => {
        setLoading(true);
        apiGet(`quota-values/${profile.id}`, user.guid, response => {
            if (!hasModule(user, 'integrations') || !ArrayHelpers.existsAndHasLength(response)) {
                onNoGoals?.();
            } else {
                const temp = sortGoalsByPeriod(response);
                setGoals(response);
                setPeriod(temp[0].period);
            }

            setLoading(false);
            !ArrayHelpers.existsAndHasLength(response) && onNoGoals && onNoGoals();
        });
    };

    React.useEffect(() => {
        setPeriodRef(period === 'W' ? 'Week' : period === 'M' ? 'Month' : period === 'Q' ? 'Quarter' : 'Year');
    }, [period]);

    React.useEffect(() => {
        setLastPaint?.(new Date());
    }, [period, goals]);

    React.useEffect(() => {
        loadGoals();
    }, [profile.id, lastUpdated]);

    const newPeriod = dir => {
        let idx = goalPeriods.findIndex(p => p === period);
        idx = idx + dir;
        idx = idx < 0 ? goalPeriods.length - 1 : idx;
        idx = idx >= goalPeriods.length ? 0 : idx;
        setPage(1);
        setPeriod(goalPeriods[idx]);
    };

    const navigationDropdown = () => {
        const periods = goals
            .reduce((a, c) => {
                return a.some(x => x === c.period) ? a : a.concat([c.period]);
            }, [])
            .map(x => ({ id: x, name: periodName(x) }));

        return (
            <div style={{ display: 'inline-block' }} data-testid="menu-goal-period">
                <FloatingMenu small className="btn" buttonText={periodName(period)}>
                    {periods.map(p => (
                        <li
                            data-testid={`period-${p.id}`}
                            key={`period-${p.id}`}
                            onClick={() => {
                                setPeriod(p.id);
                                posthog.capture('goals-period-changed', { period: p.name });
                            }}
                            className="flex flex-center"
                        >
                            {p.name}
                        </li>
                    ))}
                </FloatingMenu>
            </div>
        );
    };

    const navigation = showNavigation ? navigationDropdown() : null;

    const suffix =
        goalPeriods?.length > 1 ? (
            <span>
                <button
                    data-testid="button-previous-period"
                    aria-label="Previous Period"
                    className="btn btn-xs"
                    onClick={() => newPeriod(-1)}
                >
                    <i className="fa fa fa-arrow-left" />
                </button>
                <span className="pr10 pl10">{SystemText(`User121/Goals/GoalSummary/${periodRef}Goals`)}</span>
                <button
                    data-testid="button-next-period"
                    aria-label="Next Period"
                    className="btn btn-xs"
                    onClick={() => newPeriod(1)}
                >
                    <i className="text-sm fa fa-arrow-right" />
                </button>
            </span>
        ) : null;

    const button = (showSuffix = true) =>
        goals.length === 0 ? null : (
            <div className="flex flex-center flex-grow">
                {showSuffix && suffix}
                {!home && width > breakAt && (
                    <button
                        data-testid="button-edit-all-goals"
                        className="btn ml10 text-sm btn-primary"
                        onClick={() => {
                            setShowGoals(true);
                            setHistoric(false);
                            posthog.capture('goals-edit-all-clicked');
                        }}
                    >
                        {SystemText('User121/Goals/EditGoals')}
                    </button>
                )}
            </div>
        );

    const summaryAndEdit = (
        <>
            {goals.length > 0 ? (
                <GoalSummary
                    period={period}
                    page={page}
                    setPage={setPage}
                    periodRef={periodRef}
                    loading={loading}
                    goals={goals}
                    shown={shown}
                    showPaging={showPaging}
                    onLoad={onLoad}
                    home={home}
                    width={width}
                    breakAt={breakAt}
                    navigation={navigation}
                    setHistoric={x => {
                        setShowGoals(true);
                        setHistoric(x);
                    }}
                />
            ) : (
                <div className="mt20">{SystemText('User121/Goals/NoGoals', profile.first_Name)}</div>
            )}
            {goals.length > 0 && showGoals && (
                <SetGoals show={showGoals} historic={historic} onClose={() => setShowGoals(false)} profile={profile} />
            )}
        </>
    );

    return inBox ? (
        <Box title={SystemText('User121/Goals/Title')} button={button}>
            <div ref={container}>{summaryAndEdit}</div>
        </Box>
    ) : (
        <div ref={container}>
            {summaryAndEdit}
            <div className="mb10">{button(false)}</div>
        </div>
    );
};

export default CRMGoals;
